import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  getAllEnochEmails,
  getAllEnochMachineIps,
  getEnochConfig,
  getEnochGeneralReport,
  getEnochHistogramReport,
  getEnochProfileConnectionStats,
  getEnochProfileDetails,
  getEnochReportHistory,
  getEnochReportHistoryByVM,
  getEnochReportLogsHistory,
  getIdleEnochVMs,
  getProfilesOfVM,
  getRunningEnochVMs,
} from '../services/enoch';
import moment from 'moment';
import { enochFilterDateFormat } from '../containers/Performance/EnochPerf/common';

export const EnochQueryKeys = {
  enochConfig: 'enochConfig',
  allIdleEnochVMs: 'allIdleEnochVMs',
  allRunningEnochVMs: 'allRunningEnochVMs',
};

export const useEnochGeneralReport = ({
  email,
  ipAddress,
  fromDate,
  toDate,
  profile,
}) => {
  return useQuery(
    ['enochGeneralReport', { email, ipAddress, fromDate, toDate, profile }],
    () =>
      getEnochGeneralReport({ email, ipAddress, fromDate, toDate, profile }),
  );
};

export const useEnochProfileConnectionStats = ({
  email,
  ipAddress,
  profile,
}) => {
  return useQuery(
    ['enochProfileConnectionStats', { email, ipAddress, profile }],
    () => getEnochProfileConnectionStats({ email, ipAddress, profile }),
  );
};

export const useEnochHistogramReport = ({
  email,
  ipAddress,
  fromDate,
  toDate,
  profile,
}) => {
  const filterObject = {
    email,
    ipAddress,
    fromDate,
    toDate,
    profile,
  };
  if (moment(toDate).diff(moment(fromDate), 'days') > 100) {
    filterObject.fromDate = moment()
      .subtract(30, 'days')
      .format(enochFilterDateFormat);
    filterObject.toDate = moment().format(enochFilterDateFormat);
  }
  return useQuery(['enochHistogramReport', filterObject], () =>
    getEnochHistogramReport(filterObject),
  );
};

export const useGetEnochAllMachineIps = () => {
  return useQuery(['enochAllMachineIps'], () => getAllEnochMachineIps(), {
    initialData: [],
    keepPreviousData: true,
  });
};

export const useGetAllEnochEmails = (ipAddress?: string) => {
  return useQuery(
    ['enochEmails', { ipAddress }],
    () => getAllEnochEmails(ipAddress),
    {
      initialData: [],
      keepPreviousData: true,
    },
  );
};

export const useGetEnochConfig = (ipAddress: string) => {
  return useQuery(
    [EnochQueryKeys.enochConfig, { ipAddress }],
    () => getEnochConfig(ipAddress),
    {
      initialData: {},
      keepPreviousData: true,
      refetchOnMount: true,
    },
  );
};

export const useGetProfilesOfVM = (ipAddress: string) => {
  return useQuery(
    ['profilesOfVM', { ipAddress }],
    () => getProfilesOfVM(ipAddress),
    {
      initialData: [],
      keepPreviousData: true,
      refetchOnMount: true,
      enabled: !!ipAddress,
    },
  );
};

export const useGetEnochReportHistory = ({
  email,
  ipAddress,
  fromDate,
  toDate,
  profile,
}) => {
  return useInfiniteQuery(
    ['enochReportHistory', { email, ipAddress, fromDate, toDate, profile }],
    ({ pageParam }) =>
      getEnochReportHistory({
        email,
        ipAddress,
        fromDate,
        toDate,
        profile,
        limit: 5,
        cursor: pageParam,
      }),
    {
      getNextPageParam: lastPage => lastPage.nextCursor,
    },
  );
};

export const useGetEnochReportHistoryByVM = ({ email, ipAddress, profile }) => {
  return useInfiniteQuery(
    ['enochReportHistoryByVM', { email, ipAddress, profile }],
    ({ pageParam }) =>
      getEnochReportHistoryByVM({
        email,
        ipAddress,
        profile,
        limit: 5,
        cursor: pageParam,
      }),
    {
      getNextPageParam: lastPage => lastPage.nextCursor,
    },
  );
};

export const useGetEnochProfileDetails = ({ email, ipAddress, profile }) => {
  return useInfiniteQuery(
    ['enochProfileDetails', { email, ipAddress, profile }],
    ({ pageParam }) =>
      getEnochProfileDetails({
        email,
        ipAddress,
        profile,
        limit: 5,
        cursor: pageParam,
      }),
    {
      getNextPageParam: lastPage => lastPage.nextCursor,
    },
  );
};

export const useGetEnochReportLogsHistory = reportId => {
  return useInfiniteQuery(
    ['enochReportLogsHistory', { reportId }],
    ({ pageParam }) =>
      getEnochReportLogsHistory({
        limit: 10,
        reportId,
        cursor: pageParam,
      }),
    {
      getNextPageParam: lastPage => lastPage.nextCursor,
    },
  );
};

export const useGetIdleEnochVMs = () => {
  return useQuery([EnochQueryKeys.allIdleEnochVMs], getIdleEnochVMs, {
    initialData: [],
    keepPreviousData: true,
  });
};

export const useGetRunningEnochVMs = () => {
  return useQuery([EnochQueryKeys.allRunningEnochVMs], getRunningEnochVMs, {
    initialData: [],
    keepPreviousData: true,
  });
};
